import { ArrowUp, X } from 'lucide-react'
import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'
import { useAgentName } from './agentName'
import { AgentTab, AgentTabBar } from './AgentTabBar'
import { ArtifactsTab } from './ArtifactsTab'
import { ConnectTab } from './ConnectTab'
import { AGENT_THREAD, Message, PEOPLE, Person, PROACTIVE_MESSAGE, Speaker } from './contacts'
import { IdeasTab } from './IdeasTab'
import { MessageList } from './MessageList'

/** Roughly 9:19 against the frame's height — a phone reads tall and narrow. */
export const PANEL_WIDTH = 272

interface ChatTabProps {
  person: Person | null
  notified?: boolean
  /** Opens on nothing said yet, rather than mid-conversation. */
  blankChat?: boolean
  /** The agent's own thread, where this surface opens on something else. */
  agentThread?: Message[]
  /** Holds the thread to this width while the scroller runs the pane's full width. */
  contentWidth?: number
}

function ChatTab({ person, notified, blankChat, agentThread, contentWidth }: ChatTabProps) {
  const agentName = useAgentName()
  const ownThread = blankChat ? [] : (agentThread ?? AGENT_THREAD)
  const thread: Message[] = person ? PEOPLE[person].messages : ownThread
  // Only the agent speaks first; the people reply on their own time.
  const messages =
    notified && !person ? [...thread, { from: Speaker.BLUEY, text: PROACTIVE_MESSAGE }] : thread

  if (messages.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center p-5">
        <p className="text-ink-400 text-center text-sm">How can I help today?</p>
      </div>
    )
  }

  return (
    <MessageList
      fadeTop
      contentWidth={contentWidth}
      messages={messages}
      names={person ? { agent: agentName, person: PEOPLE[person].name } : undefined}
    />
  )
}

interface AgentSurfaceProps {
  /** The person in the room alongside Bluey, or null for Bluey alone. */
  person: Person | null
  notified?: boolean
  /** Adds the Connect tab — the fleet assistant's fourth surface. */
  connect?: boolean
  /** Opens on nothing said yet, rather than mid-conversation. */
  blankChat?: boolean
  /** Holds the thread and composer to this width; the tab bar still spans the pane. */
  contentWidth?: number
  /** Opens on a surface other than the chat. */
  initialTab?: AgentTab
  /** Raised when someone asks to add a person from the Connect tab. */
  onInvite?: () => void
  /** Drops the tab bar, for an agent with only the one surface. */
  hideTabBar?: boolean
  /** A note above the thread — what this agent is, or is about to become. */
  banner?: React.ReactNode
  /** The agent's own thread, where this surface opens on something else. */
  agentThread?: Message[]
  /** Replaces "Message <name>", where this surface is not the account's own chat. */
  composerLabel?: string
  /** Hides the thread and composer while the pane resizes around them. */
  bodyFaded?: boolean
  /** The agent cannot help here: the thread goes quiet, the composer says why. */
  disabled?: boolean
  /** Sits under the composer — trial state, and the like. */
  composerFooter?: React.ReactNode
  /** The setup flow's composer: a recessed surface, rounder, green to send. */
  softComposer?: boolean
  /** Bumped to send the pane back to the conversation, wherever it had got to. */
  showChat?: number
}

/**
 * Everything below the contact strip: the tabbed body, the composer and the bar
 * that switches between them. Shared, so the panel docked in the web app and
 * the React app's own screen cannot drift apart.
 */
export function AgentSurface({
  person,
  notified,
  connect,
  blankChat,
  contentWidth,
  initialTab,
  onInvite,
  hideTabBar,
  banner,
  agentThread,
  composerLabel,
  bodyFaded,
  disabled,
  composerFooter,
  softComposer,
  showChat,
}: AgentSurfaceProps) {
  const [tab, setTab] = useState<AgentTab>(initialTab ?? AgentTab.CHAT)
  // An artifact carried into the composer: editing one is a thing you ask for, not a form.
  const [attached, setAttached] = useState<string | null>(null)
  const agentName = useAgentName()
  const addressee = person ? `${PEOPLE[person].name} & ${agentName}` : agentName
  const column = contentWidth ? { maxWidth: contentWidth } : undefined

  // Leaving the agent's own page leaves the conversation open behind you:
  // coming back to a docked Ideas list you opened ten minutes ago is not
  // where anyone wants to resume.
  useEffect(() => {
    if (showChat) setTab(AgentTab.CHAT)
  }, [showChat])

  return (
    <>
      {/* A column inside the pane: the conversation stays readable while the bar
          below it runs the pane's full width. */}
      <div
        className={cn(
          'flex min-h-0 w-full flex-1 flex-col transition-opacity duration-150 ease-out',
          bodyFaded && 'opacity-0'
        )}
      >
        {banner && (
          <p
            className="bg-ink-50 text-ink-500 mx-auto w-full rounded-md px-2 py-1.5 text-center text-[10px] leading-snug"
            style={column}
          >
            {banner}
          </p>
        )}
        {tab === AgentTab.CHAT && (
          <div className={cn('flex min-h-0 flex-1 flex-col', disabled && 'pointer-events-none opacity-40')}>
            <ChatTab
              person={person}
              notified={notified}
              blankChat={blankChat}
              agentThread={agentThread}
              contentWidth={contentWidth}
            />
          </div>
        )}
        {/* The other surfaces scroll their own content, so they keep the column. */}
        {tab === AgentTab.CONNECT && <ConnectTab onInvite={onInvite} contentWidth={contentWidth} />}
        {tab === AgentTab.IDEAS && <IdeasTab contentWidth={contentWidth} />}
        {tab === AgentTab.ARTIFACTS && (
          <ArtifactsTab
            contentWidth={contentWidth}
            onEdit={(label) => {
              setAttached(label)
              setTab(AgentTab.CHAT)
            }}
          />
        )}

        {tab === AgentTab.CHAT && (
          <div className="mx-auto w-full p-2" style={column}>
            <div
              className={cn(
                'flex flex-col gap-2 border px-3 py-2',
                softComposer
                  ? 'border-input bg-surface hover:border-ink-300 rounded-2xl transition-colors'
                  : 'border-ink-100 rounded-xl'
              )}
            >
              {attached && (
                <span className="border-ink-100 bg-ink-50 flex items-center gap-1.5 self-start rounded-md border py-1 pr-1 pl-1.5">
                  <span className="bg-ink-100 size-4 shrink-0 rounded-sm" aria-hidden />
                  <span className="text-ink text-[10px]">{attached}</span>
                  <button
                    type="button"
                    onClick={() => setAttached(null)}
                    aria-label={`Remove ${attached}`}
                    className="hover:bg-ink-100 rounded p-0.5"
                  >
                    <X className="text-ink-400 size-3" />
                  </button>
                </span>
              )}
              <div className="flex items-center gap-2">
                <span className="text-ink-400 flex-1 truncate text-xs">
                  {attached
                    ? 'Describe the change you want or question you have...'
                    : (composerLabel ?? `Message ${addressee}`)}
                </span>
                <span
                  className={cn(
                    'flex size-6 shrink-0 items-center justify-center rounded-full',
                    softComposer ? 'bg-brand' : 'bg-ink'
                  )}
                >
                  <ArrowUp className={cn('size-3.5', softComposer ? 'text-ink-900' : 'text-white')} />
                </span>
              </div>
            </div>
            {composerFooter}
          </div>
        )}
      </div>

      {!hideTabBar && (
        <div className={cn(disabled && 'pointer-events-none opacity-40')}>
          <AgentTabBar tab={tab} onSelect={setTab} connect={connect} />
        </div>
      )}
    </>
  )
}
